import { t } from "@lingui/macro";
import {
  postEvent,
  useCloudContext,
  useConfigContext,
  useGuardedUpdater,
  useNativeMessagingContext,
  useOfflineSWR,
} from "@WahooFitness/wahoo-offline-mfe";
import { useCallback, useEffect, useMemo } from "react";
import { NotificationEventSettings } from "@WahooFitness/cloud-client-ts";
import { useWsmPlugin } from "@/hooks/useWsmPlugin";
import WFNativeAppPermissionsType from "@WahooFitness/wsm-native/dist/esm/types/wf_native_app_permissions_type";
import useSWR from "swr";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { ClickTarget, EventType, SourcePage } from "@/services/Analytics/EventTypes";

const SLEEP_RECOMMENDATION_EVENT_ID = 7;

// Cloud has no default send time, so the app picks one. Tonight's sleep need goes out in the evening.
const getDefaultSendTime = (eventId: number) =>
  eventId === SLEEP_RECOMMENDATION_EVENT_ID ? "18:00" : "10:00";

const getNotificationTypeText = (eventTypeId: number): [string, string] => {
  switch (eventTypeId) {
    case 1:
      return [
        t`General`,
        t`Important updates about your account, hardware, and app functionality.`,
      ];
    case 2:
      return [
        t`Activities & workouts`,
        t`Notifications related to completed workouts, ride uploads, and activity sync status.`,
      ];
    case 3:
      return [
        t`Training progress`,
        t`Updates about your training plan, fitness milestones, and performance trends.`,
      ];
    case 4:
      return [
        t`Training Insights`,
        t`Notifications related to training and insights tied to your recovery.`,
      ];
    default:
      return [t`Other`, t`Other notification types.`];
  }
};

const useNotifications = () => {
  const { wahooToken } = useConfigContext();
  const { getCloudClient } = useCloudContext();
  const notificationSettingsClient = getCloudClient(NotificationEventSettings);
  const { update, updateInProgress } = useGuardedUpdater();
  const { sensorManagerPlugin } = useWsmPlugin();
  const { addRefreshListener, removeRefreshListener } = useNativeMessagingContext();
  const { logEvent } = useLogEvent();

  const { data: notificationsEnabled, mutate: recheckNotificationsEnabled } = useSWR(
    ["notificationsEnabled"],
    async () => {
      const result = await sensorManagerPlugin.getNativeAppPermissionEnabled({
        nativeAppPermission: WFNativeAppPermissionsType.PUSH_NOTIFICATIONS,
      });
      return result.enabled;
    },
    { fallbackData: true }
  );

  useEffect(() => {
    const refreshListener = addRefreshListener(recheckNotificationsEnabled);
    return () => removeRefreshListener(refreshListener);
  }, [addRefreshListener, recheckNotificationsEnabled, removeRefreshListener]);

  const {
    data: notificationSettings,
    isLoading: notificationSettingsLoading,
    error: notificationSettingsError,
    mutate: mutateNotificationSettings,
  } = useOfflineSWR(
    wahooToken && [wahooToken, "notificationSettings"],
    () => notificationSettingsClient.get(wahooToken),
    { fallbackData: [] }
  );

  const requestNotificationPermission = useCallback(() => {
    postEvent("requestPermission", {
      permissionType: "pushNotifications",
    });
  }, []);

  const notificationTypes = useMemo((): {
    id: number;
    name: string;
    description: string;
  }[] => {
    const categories = new Set<number>();
    const categoryList: { id: number; name: string; description: string }[] = [];

    notificationSettings?.forEach((setting) => {
      if (!categories.has(setting.notification_event_type_id)) {
        const [name, description] = getNotificationTypeText(setting.notification_event_type_id);
        categories.add(setting.notification_event_type_id);
        categoryList.push({ id: setting.notification_event_type_id, name, description });
      }
    });

    return categoryList.sort((a, b) => a.id - b.id);
  }, [notificationSettings]);

  const getNotificationType = useCallback(
    (eventTypeId: number) => {
      return notificationTypes.find((type) => type.id === eventTypeId);
    },
    [notificationTypes]
  );

  const getNotificationSettingsForType = useCallback(
    (eventTypeId: number) => {
      if (!notificationSettings) {
        return [];
      }
      return notificationSettings.filter(
        (setting) => setting.notification_event_type_id === eventTypeId
      );
    },
    [notificationSettings]
  );

  const updateNotificationSetting = useCallback(
    (
      eventId: number,
      updates: Partial<{ email: boolean; push_notification: boolean; send_time: string | null }>
    ) => {
      const currentSetting = notificationSettings?.find(
        (setting) => setting.notification_event_id === eventId
      );
      if (!currentSetting) {
        return;
      }
      update(
        () =>
          notificationSettingsClient.update(wahooToken, {
            notification_event_id: eventId,
            ...updates,
          }),
        {
          afterUpdate: (updatedSetting) =>
            mutateNotificationSettings(
              notificationSettings?.map((setting) => {
                if (setting.notification_event_id === eventId) {
                  return { ...setting, ...updatedSetting };
                }
                return setting;
              })
            ),
        }
      );
    },
    [
      mutateNotificationSettings,
      notificationSettings,
      notificationSettingsClient,
      update,
      wahooToken,
    ]
  );

  const toggleNotificationSetting = useCallback(
    (
      eventId: number,
      notificationType: "email" | "push_notification",
      sendTime?: string | null
    ) => {
      const setting = notificationSettings?.find((s) => s.notification_event_id === eventId);
      const currentSetting = setting?.[notificationType];
      if (currentSetting === undefined) {
        return;
      }

      const updates: Partial<{
        email: boolean;
        push_notification: boolean;
        send_time: string | null;
      }> = {
        [notificationType]: !currentSetting,
      };

      // If turning on and scheduled but no send_time set, use provided time or the event's default
      if (!currentSetting && setting?.scheduled && setting.send_time === null) {
        if (sendTime !== undefined) {
          updates.send_time = sendTime;
        } else {
          updates.send_time = getDefaultSendTime(eventId);
        }
      }

      if (notificationType === "push_notification" && setting) {
        logEvent("push_notification_management", {
          event_type: EventType.click,
          click_target: currentSetting ? ClickTarget.toggle_off : ClickTarget.toggle_on,
          source_page_name: SourcePage.notifications,
          push_notification_id: eventId,
        });
      }

      updateNotificationSetting(eventId, updates);
    },
    [logEvent, notificationSettings, updateNotificationSetting]
  );

  const toggleEmailNotification = useCallback(
    (eventId: number, sendTime?: string | null) => {
      toggleNotificationSetting(eventId, "email", sendTime);
    },
    [toggleNotificationSetting]
  );

  const togglePushNotification = useCallback(
    (eventId: number, sendTime?: string | null) => {
      toggleNotificationSetting(eventId, "push_notification", sendTime);
    },
    [toggleNotificationSetting]
  );

  const updateSendTime = useCallback(
    (eventId: number, sendTime: string | null) => {
      updateNotificationSetting(eventId, { send_time: sendTime });
    },
    [updateNotificationSetting]
  );

  const convertTimeStringToDate = useCallback((timeString: string | null): Date | null => {
    if (!timeString) {
      return null;
    }

    // Parse the time string (handles both "10:00:00" and "10:00" formats)
    const parts = timeString.split(":");
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    const seconds = parts[2] !== undefined ? Number(parts[2]) : 0;

    const date = new Date();
    date.setHours(hours, minutes, seconds, 0);

    return date;
  }, []);

  const convertDateToTimeString = useCallback((date: Date | null): string | null => {
    if (!date) {
      return null;
    }

    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
  }, []);

  return {
    notificationSettings,
    notificationSettingsLoading,
    notificationSettingsError,
    notificationTypes,
    getNotificationType,
    getNotificationSettingsForType,
    toggleEmailNotification,
    togglePushNotification,
    updateSendTime,
    convertTimeStringToDate,
    convertDateToTimeString,
    notificationUpdateInProgress: updateInProgress,
    notificationsEnabled,
    requestNotificationPermission,
  };
};

export { useNotifications };
